import { LogsButton } from "@/components/Button";
import TitleHeader from "@/components/TitleHeader";
import { Flex, Text, Image, useDisclosure, Input, Box } from "@chakra-ui/react";
import SelectAvatar from "../org/SelectAvatar";
import { useUserInfo } from "@/context/UserInfo";
import { useCallback, useEffect, useRef, useState } from "react";
import { createBundler } from "@/constants/bundler";
import { didAddresses } from "@/constants/addresses";
import { DEFAULT_CHAIN_ID } from "@/constants";
import {
  encodeFunctionData,
  keccak256,
  toBytes,
  toHex,
  zeroAddress,
} from "viem";
import capsule from "@/constants/capsule";
import usePublicClient from "@/hooks/usePublicClient";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import LogsDidAbi from "@/constants/abis/LogsDid.json";
import ErrorIcon from "@/assets/error.png";

const RequestCreatedTopic = keccak256(
  toBytes("RequestCreated(uint256,address,uint256,bytes)")
);

const EditAvatar = () => {
  const { avatarIndex, smartAccount, handleSetAvatarIndex, handleSetOrgName } =
    useUserInfo();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [currentAvatarIndex, setCurrentAvatarIndex] = useState(avatarIndex);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [orgName, setOrgName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const [isOrgNameAvailable, setIsOrgNameAvailable] = useState<boolean>(false);
  const [alreadyInput, setAlreadyInput] = useState<boolean>(false);
  const publicClient = usePublicClient();

  const handleOnAvatarChange = (index: number) => {
    setCurrentAvatarIndex(index);
    onClose();
  };

  const handleConfirm = async (orgName: string) => {
    try {
      const isActive = await capsule.isSessionActive();
      if (!isActive) {
        // Handle expired session
        await capsule.refreshSession(false);
        return;
      }
    } catch (error) {
      console.error("Session check failed:", error);
    }

    setLoading(true);

    const transferCallData = await smartAccount!.encodeCalls([
      {
        to: didAddresses[DEFAULT_CHAIN_ID]!,
        value: 0n,
        data: encodeFunctionData({
          abi: [
            {
              type: "function",
              name: "registerSelf",
              inputs: [
                {
                  name: "name",
                  type: "bytes32",
                  internalType: "bytes32",
                },
              ],
              outputs: [],
              stateMutability: "nonpayable",
            },
          ],
          args: [toHex(orgName, { size: 32 })],
        }),
      },
    ]);

    try {
      const bundler = await createBundler(smartAccount!);
      const tx = await bundler.sendTransaction(transferCallData);
      const status = await tx.wait();
      const txHash = status.transactionHash;

      const receipt = await publicClient.waitForTransactionReceipt({
        hash: txHash as `0x${string}`,
      });

      const requestCreatedLog = receipt.logs.find((log) => {
        // 这里需要填入 RequestCreated 事件的实际 topic0
        return log.topics[0] === RequestCreatedTopic;
      });

      if (requestCreatedLog) {
        // 解析日志中的 id（第一个索引参数）
        const requestId = BigInt(requestCreatedLog.topics[1]!);
        console.log("Request ID:", requestId);
      }

      handleSetOrgName(orgName);
      navigate("/myInfo");
    } catch (error) {
      console.error("发送失败:", error);
    } finally {
      setLoading(false);
    }
  };

  const validateInput = (value: string): boolean => {
    // 只允许字母、数字和普通空格
    return /^[a-zA-Z0-9 ]*$/.test(value);
  };

  // 创建检查组织名称是否可用的函数
  const checkOrgNameAvailability = async (name: string) => {
    try {
      console.log("进来name", name);
      // 这里需要根据你的具体合约调用方法来修改
      const ownerAddress = await publicClient.readContract({
        // 添加你的合约相关参数
        address: didAddresses[DEFAULT_CHAIN_ID]!,
        abi: LogsDidAbi,
        functionName: "resolveAddr",
        args: [toHex(name, { size: 32 })],
      });
      setIsOrgNameAvailable(ownerAddress === zeroAddress);
    } catch (error) {
      console.error("Error checking org name:", error);
      setIsOrgNameAvailable(false);
    }
  };

  // 使用useCallback和debounce创建防抖函数
  const debouncedCheck = useCallback(
    debounce((name: string) => {
      if (name) {
        checkOrgNameAvailability(name);
      } else {
        console.log("直接false");
        setIsOrgNameAvailable(false);
      }
    }, 500), // 500ms的防抖延迟
    []
  );

  // 修改输入处理函数
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!alreadyInput) {
      setAlreadyInput(true);
    }
    const value = e.target.value;
    if (validateInput(value) && value.length <= 32) {
      setOrgName(value);
      debouncedCheck(value);
    }
  };

  // 在组件卸载时取消未执行的防抖函数
  useEffect(() => {
    return () => {
      debouncedCheck.cancel();
    };
  }, []);

  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
      inputRef.current?.click();
    }, 50);
  }, []);

  return (
    <Flex
      flexDir={"column"}
      align={"center"}
      sx={{ padding: "20px", position: "relative", height: "100%" }}
    >
      <TitleHeader
        title="Set Name"
        onBack={() => {
          navigate("/myInfo");
        }}
        color="#D8762B"
      ></TitleHeader>
      <Text
        sx={{
          width: "100%",
          marginTop: "40px",
        }}
      >
        Set Organization Name{" "}
      </Text>
      <Box
        sx={{
          width: "100%",
          marginTop: "3px",
          height: "20px",
        }}
      >
        {!isOrgNameAvailable && alreadyInput && (
          <Flex align={"center"} gap={"4px"} sx={{}}>
            <Image
              src={ErrorIcon}
              sx={{
                width: "12px",
                height: "100%",
              }}
            ></Image>
            <Text
              sx={{
                color: "#D60000",
                fontSize: "12px",
                fontWeight: "bold",
                fontStyle: "italic",
              }}
            >
              Organization Name Error
            </Text>
          </Flex>
        )}
      </Box>

      <Input
        ref={inputRef}
        variant={"unstyled"}
        placeholder="Input name"
        value={orgName}
        onChange={handleInputChange}
        sx={{
          width: "100%",
          height: "20px",
          border: "1px solid transparent",
          borderRadius: "4px",
          fontSize: "14px",
          padding: "0 0px",
          background: "transparent",
          caretColor: "#D8762B",
          position: "relative",
          marginTop: "4px",
          color: isOrgNameAvailable ? "#333" : "#D60000",
          _placeholder: {
            color: "#dedede",
            fontSize: "14px",
          },
        }}
      />
      <LogsButton
        loading={loading}
        onClick={() => {
          if (isOrgNameAvailable) {
            handleConfirm(orgName);
          }
        }}
        sx={{
          marginTop: "auto",
          background: isOrgNameAvailable ? "#D8762B" : "#BBB",
          border: isOrgNameAvailable ? "1px solid #D8762B" : "1px solid #BBB",
        }}
      >
        Confirm
      </LogsButton>
      <SelectAvatar
        isOpen={isOpen}
        onClose={onClose}
        handleOnAvatarChange={handleOnAvatarChange}
      />
    </Flex>
  );
};

export default EditAvatar;
